const EARTH_R = 6371000; // meters
const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/** Great-circle distance in meters between two [lat, lng] points. */
export function haversineMeters(a: [number, number], b: [number, number]): number {
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Point [lat, lng] reached by traveling `meters` from (lat,lng) along `bearingDeg` (0=N, 90=E). */
export function destinationPoint(lat: number, lng: number, meters: number, bearingDeg: number): [number, number] {
  const d = meters / EARTH_R;
  const brng = toRad(bearingDeg);
  const lat1 = toRad(lat);
  const lng1 = toRad(lng);
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng));
  const lng2 = lng1 + Math.atan2(Math.sin(brng) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return [toDeg(lat2), ((toDeg(lng2) + 540) % 360) - 180];
}

// Ray-casting point-in-polygon. Points are [lat, lng]; treated planar (fine at
// county scale). The ring need not repeat its first vertex.
export function pointInPolygon(pt: [number, number], ring: [number, number][]): boolean {
  const [y, x] = pt; // lat=y, lng=x
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i][0], xi = ring[i][1], yj = ring[j][0], xj = ring[j][1];
    const intersect = (xi > x) !== (xj > x) && y < ((yj - yi) * (x - xi)) / (xj - xi) + yi;
    if (intersect) inside = !inside;
  }
  return inside;
}
